// MovableItemsContainer contains all robots and other movable
// items and coordinates their movements.

import HaltActionResult from 'app/src/engine/action-results/HaltActionResult';
import MoveMovableItemActionResult, {
	MoveMovableItemActionResultDirection,
	MoveMovableItemActionResultResult,
} from 'app/src/engine/action-results/MoveMovableItemActionResult';
import MovableItem, {
	MovableItemLocation,
	MovableItemOrientation,
} from 'app/src/engine/movable-items/MovableItem';
import { useGameStateContext } from 'app/src/game/GameStateContext';
import { GameFinishedReason } from 'app/src/game/GameStateStore';
import scheduleAnimation from 'app/src/utils/scheduleAnimation';
import useValueChanged from 'app/src/utils/useValueChanged';
import * as React from 'react';

const styles = {
	boardContainer: {
		base: {
			position: 'absolute',
			width: '100px',
			height: '100px',
		} as React.CSSProperties,
		gameActive: {
			transition: 'transform 600ms linear',
		} as React.CSSProperties,
	},
	localContainer: {
		width: '100px',
		height: '100px',
		transition: 'transform 200ms linear',
	} as React.CSSProperties,
};

type Props = {
	movableItem: MovableItem;
};

// Determine if the robot is facing the direction it is moving.
function movableItemOrientationMatchesActionDirection(
	orientation: MovableItemOrientation,
	actionDirection: MoveMovableItemActionResultDirection,
): boolean {
	return (
		(orientation === MovableItemOrientation.Up &&
			actionDirection === MoveMovableItemActionResultDirection.Up) ||
		(orientation === MovableItemOrientation.Left &&
			actionDirection === MoveMovableItemActionResultDirection.Left) ||
		(orientation === MovableItemOrientation.Down &&
			actionDirection === MoveMovableItemActionResultDirection.Down) ||
		(orientation === MovableItemOrientation.Right &&
			actionDirection === MoveMovableItemActionResultDirection.Right)
	);
}

export default function MovableItemWrapper({ movableItem }: Props): React.ReactElement | null {
	const { gameStateStore } = useGameStateContext();

	// Get current location.
	const movableItemLocation = gameStateStore.movableItemLocations.get(movableItem)!;

	// Maintain any overrides due to action results.
	const [
		overrideBoardLocation,
		setOverrideBoardLocation,
	] = React.useState<MovableItemLocation | null>(null);
	const [
		overrideLocalLocation,
		setOverrideLocalLocation,
	] = React.useState<MovableItemLocation | null>(null);
	const [fallen, setFallen] = React.useState(false);

	// When we're running animations, we need to be able to cancel.
	const cancelAnimationSequenceRef = React.useRef<(() => void) | null>(null);

	// If this component is unmounted (this happens when the user resets), kill
	// any ongoing animations.
	React.useEffect(
		() => (): void => {
			if (cancelAnimationSequenceRef.current) {
				cancelAnimationSequenceRef.current();
			}
		},
		[],
	);

	// Handle animation changes.
	const currentActionResultSetChanged = useValueChanged(gameStateStore.currentActionResultSet);
	React.useEffect(() => {
		const { currentActionResultSet } = gameStateStore;

		if (currentActionResultSetChanged && currentActionResultSet) {
			// Is there a MoveMovableItemActionResult intended for this node?
			const moveMovableItemActionResult = currentActionResultSet.find(
				(result) =>
					result instanceof MoveMovableItemActionResult && result.movableItem === movableItem,
			) as (MoveMovableItemActionResult | undefined);

			// Is there a HaltAction intended for this node?
			const haltActionResult = currentActionResultSet.find(
				(result) => result instanceof HaltActionResult && result.movableItem === movableItem,
			) as (HaltActionResult | undefined);

			// If the action result is ours, animate.
			if (moveMovableItemActionResult) {
				if (moveMovableItemActionResult.movementResult === MoveMovableItemActionResultResult.Fall) {
					const newLocation = moveMovableItemActionResult.newLocation;
					setOverrideBoardLocation(newLocation);

					cancelAnimationSequenceRef.current = scheduleAnimation([
						[
							(): void => {
								setFallen(true);
							},
							400,
						],
						[
							(): void => {
								setOverrideBoardLocation(null);
								gameStateStore.completeAnimation();
								cancelAnimationSequenceRef.current = null;
							},
							200,
						],
					]);
				} else if (
					moveMovableItemActionResult.movementResult === MoveMovableItemActionResultResult.Move
				) {
					const newLocation = moveMovableItemActionResult.newLocation;
					setOverrideBoardLocation(newLocation);

					cancelAnimationSequenceRef.current = scheduleAnimation([
						[
							(): void => {
								setOverrideBoardLocation(null);
								gameStateStore.completeAnimation();
								cancelAnimationSequenceRef.current = null;
							},
							600,
						],
					]);
				} else if (
					moveMovableItemActionResult.movementResult === MoveMovableItemActionResultResult.Bounce
				) {
					// Which direction is the robot going to bump?
					const bounceDirection = movableItemOrientationMatchesActionDirection(
						movableItemLocation.orientation,
						moveMovableItemActionResult.movementDirection,
					)
						? -1
						: 1;
					const bounceLocation = { ...movableItemLocation, row: bounceDirection * 0.2, col: 0 };
					setOverrideLocalLocation(bounceLocation);

					cancelAnimationSequenceRef.current = scheduleAnimation([
						[
							(): void => {
								setOverrideLocalLocation(null);
							},
							200,
						],
						[
							(): void => {
								gameStateStore.completeAnimation();
								cancelAnimationSequenceRef.current = null;
							},
							400,
						],
					]);
				}
			} else if (haltActionResult) {
				cancelAnimationSequenceRef.current = scheduleAnimation([
					[
						(): void => {
							gameStateStore.completeAnimation();
							cancelAnimationSequenceRef.current = null;
						},
						200,
					],
				]);
			}
		}
	}, [currentActionResultSetChanged, gameStateStore, movableItem, movableItemLocation]);

	// Grab the provider for this node use use it to build the node.
	const providerGamePlugin = Array.from(gameStateStore.gamePlugins).find((gamePlugin) =>
		gamePlugin.providesNodeForMovableItem(movableItem),
	);

	if (!providerGamePlugin) {
		throw new Error('Cannot find game plugin for movable item.');
	}

	const movableItemNode = providerGamePlugin.getMovableItemNode(movableItem);

	const referenceLocation = overrideBoardLocation || movableItemLocation;

	return (
		<div
			style={{
				...styles.boardContainer.base,
				...(gameStateStore.gameFinishedReason === GameFinishedReason.Reset
					? {}
					: styles.boardContainer.gameActive),
				transform: `translate(${referenceLocation.col * 100}px, ${referenceLocation.row *
					100}px) rotate(${referenceLocation.turns * 90}deg)`,
			}}
		>
			<div
				style={{
					...styles.localContainer,
					transform: `translate(${
						overrideLocalLocation
							? `${overrideLocalLocation.col * 100}px, ${overrideLocalLocation.row * 100}px`
							: '0, 0'
					}) scale(${fallen ? '0, 0' : '1, 1'})`,
				}}
			>
				{movableItemNode}
			</div>
		</div>
	);
}
